import * as binderTypes from "../enums/binderTypes";
import * as libraryUtil from "../lib/libraryUtil";
import * as qs from "./queryString";
import * as queryStringFields from "../enums/queryStringFields";
import safeEncodeURIComponent from "./safeEncodeURIComponent";
import store from "../lib/store";
import * as selectorFactory from "../lib/selectorFactory";
import * as libraryNames from "../enums/libraryNames";

const currentUser = selectorFactory.createGetCurrentUser();

export const getNavigationWithLibrary = (libraryName, url) => {
	const userLibraries = currentUser(store.getState())
		? currentUser(store.getState()).practice_areas
		: [];
	const activeLibraries = userLibraries.filter(library => library.active);
	const firstUserLibrary = libraryUtil.getFirstLibraryName(false, activeLibraries);

	if (!libraryName || !libraryName.length) {
		return firstUserLibrary ? `/${firstUserLibrary}/${url}` : `/patent/account-settings`;
	}
	return `/${libraryName}/${url}`;
};

export const DOCKET_REPORT_ARCHIVE = "docket-report-archive";
export const MY_RESEARCH = "my-research";
const ALERTS = "alerts";
const USAGE = "usage";
const GROUP = "group";

export function getBinderPath(binder) {
	const binderType = binder.type ? binder.type : binderTypes.BINDER;
	const libraryName = binder.libraryName || binder.library_name;
	return libraryName === libraryNames.UPC
		? `${binder?.content?.url}${binder.id}`
		: `/${libraryName}/${binderType}/${binder.id}`;
}

export function getBinderPathFromParts(libraryName, binderType, binderId) {
	return `/${libraryName}/${binderType}/${binderId}`;
}

export function getBinderTabPath(binder, tabId) {
	return `${getBinderPath(binder)}/${tabId}`;
}

export function getBinderTabPathFromParts(libraryName, binderType, binderId, tabId) {
	return `/${libraryName}/${binderType}/${binderId}/${tabId}`;
}

export function getBinderProfileTabPath(binder, tabIndex = 0) {
	return `${getBinderPath(binder)}/${tabIndex}`;
}

export function getBinderProfilePathFromParts(libraryName, profileType, profileId) {
	return `/${libraryName}/${profileType}/${profileId}`;
}

export function getBinderProfileTabPathFromParts(libraryName, profileType, profileId) {
	return `/${libraryName}/${profileType}/${profileId}/0`;
}

export function getSearchPath(libraryName) {
	return `/${libraryName}/search`;
}

export function getActiveReportSearchPath(libraryName, reportName, comparisonReport = false) {
	return `/${libraryName}/search/${comparisonReport ? "comparison" : "report"}/${reportName}`;
}

export function getActiveSearchPath(libraryName, targetName) {
	return `/${libraryName}/search/${targetName}`;
}

export function getMyResearchPath(libraryName) {
	return getNavigationWithLibrary(libraryName, MY_RESEARCH);
}

export function getFirstLibraryMyResearchPath(practiceAreas) {
	const libraryName = libraryUtil.getFirstLibraryName(false, practiceAreas);
	return getMyResearchPath(libraryName);
}

export function getCuratedPath(libraryName) {
	return getNavigationWithLibrary(libraryName, "curated-binders");
}

export function getDocketReportArchivePath(libraryName) {
	return getNavigationWithLibrary(libraryName, DOCKET_REPORT_ARCHIVE);
}

export function parseBinderPath(path) {
	if (!path) {
		return { binderId: null, tabId: null };
	}

	const paths = path.split("/");
	const binderId = paths.length >= 4 ? parseInt(paths[3], 10) : null;
	const tabId = paths.length >= 5 ? parseInt(paths[4], 10) : null;
	return { binderId, tabId };
}

export function getAlertsPath(libraryName) {
	return getNavigationWithLibrary(libraryName, ALERTS);
}

export function getFirstLibraryAlertsPath(practiceAreas) {
	const libraryName = libraryUtil.getFirstLibraryName(false, practiceAreas);
	return getAlertsPath(libraryName);
}

export function getUsagePath(libraryName) {
	return `/${libraryName}/${USAGE}`;
}

export function getFirstLibraryUsagePath(isAdmin, practiceAreas, groupLibraries) {
	const libraryName = libraryUtil.getFirstLibraryName(isAdmin, practiceAreas, groupLibraries);
	return getUsagePath(libraryName);
}

export function getGroupPath(libraryName) {
	return getNavigationWithLibrary(libraryName, GROUP);
}

export function getFirstLibraryGroupPath(isAdmin, practiceAreas, groupLibraries) {
	const libraryName = libraryUtil.getFirstLibraryName(isAdmin, practiceAreas, groupLibraries);
	return getGroupPath(libraryName);
}

export function getBinderTabPrintPathFromParts(
	libraryName,
	binderType,
	binderId,
	tabId,
	expandAll,
	expandedRows,
) {
	let query = `?${queryStringFields.PRINT}=true`;
	if (expandAll === true || expandAll === false) {
		query += `&${queryStringFields.EXPAND_ALL}=${expandAll}`;
	}
	if (expandedRows && Object.keys(expandedRows).length) {
		Object.keys(expandedRows).forEach(idx => {
			const isExpanded = expandedRows[idx];
			const qsKey = isExpanded
				? queryStringFields.EXPAND_IDXS
				: queryStringFields.COLLAPSE_IDXS;
			query += `&${qsKey}=${idx}`;
		});
	}

	return `${getBinderTabPathFromParts(libraryName, binderType, binderId, tabId)}${query}`;
}

export function getBinderPrintPath(binder) {
	return `${getBinderPath(binder)}/print`;
}

export function getHomePath(libraryName, defaultStartTargetName, targetsLoaded, targets) {
	if (!libraryName) {
		return accountSettings(libraryName);
	}
	if (!defaultStartTargetName) {
		return getSearchPath(libraryName);
	}

	const targetPath = `${libraryName}${defaultStartTargetName}`;
	if (targetsLoaded && !targets[targetPath]) {
		return getSearchPath(libraryName);
	}

	return getActiveSearchPath(libraryName, targetPath);
}

export function getLoginRedirect(pathname, queryString) {
	if (pathname && pathname !== "/") {
		const destination = safeEncodeURIComponent(pathname, "");
		const queryStringParams = qs.parse(queryString);
		queryStringParams.destination = destination;
		return `${login()}?${qs.stringify(queryStringParams)}`;
	}

	return login();
}

export function accountSettings(libraryName) {
	return getNavigationWithLibrary(libraryName, "account-settings");
}

export function litigationHistory(libraryName) {
	return getNavigationWithLibrary(libraryName, "litigation-history");
}

export function dev() {
	return "/dev";
}

export function error() {
	return "/error";
}

export function forgotPassword() {
	return "/forgot-password";
}

export function freeTrial() {
	return "/free-trial";
}

export function login() {
	return "/login";
}

export function newGroup() {
	return "/new-group";
}

export function newGroupSubmissionConfirmation() {
	return "/new-group-submission-confirmation";
}

export function newGroupUser() {
	return "/new-group-user";
}

export function newIndividualUser() {
	return "/choose-libraries";
}

export function newUnlimitedGroupUser() {
	return "/new-unlimited-group-user/:groupToken";
}

export function notFound() {
	return "/not-found";
}

export function requestAccess() {
	return "/request-access";
}

export function resetPassword() {
	return "/reset-password";
}

export function unauthorized() {
	return "/unauthorized";
}

export function welcome() {
	return "/welcome";
}

export function downloads(libraryName) {
	return getNavigationWithLibrary(libraryName, "downloads");
}

export function contactUs(libraryName) {
	return getNavigationWithLibrary(libraryName, "contact-us");
}

export function memberList(libraryName) {
	return getNavigationWithLibrary(libraryName, "all-members-list");
}

export function drpPublication(publicationId) {
	return `/api/documents/publication/${publicationId}`;
}
